import React, { useState, useEffect, useRef } from 'react';
import { Send, Check, Loader } from 'lucide-react';
import axios from 'axios';
import { trackGoal, trackEvent, GOALS } from '../../utils/yandexMetrika';
import LegalNotice from '../LegalNotice/LegalNotice';
import './ContactForm.css';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '+7',
    email: '',
    website: '',
    agreedPdn: false,    // Согласие на обработку ПДн (152-ФЗ ст. 9)
    agreedPolicy: false  // Согласие с политикой конфиденциальности
  });
  
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [error, setError] = useState('');
  const [formStarted, setFormStarted] = useState(false);
  const formOpenedAt = useRef(Date.now());

  // Отслеживаем просмотр формы
  useEffect(() => {
    trackGoal(GOALS.FORM_OPEN);
    trackEvent('Form', 'View', 'Contact Form');
  }, []);

  // Форматирование номера телефона
  const formatPhoneNumber = (value) => {
    // Удаляем все символы кроме цифр
    let phoneNumber = value.replace(/\D/g, '');
    
    // Если номер пустой, возвращаем +7
    if (phoneNumber.length === 0) return '+7';
    
    // Если номер не начинается с 7, добавляем 7 в начало
    if (!phoneNumber.startsWith('7')) {
      phoneNumber = '7' + phoneNumber;
    }
    
    // Ограничиваем длину номера (11 цифр максимум для российского номера)
    phoneNumber = phoneNumber.slice(0, 11);
    
    const phoneNumberLength = phoneNumber.length;
    
    // Форматируем номер
    let formattedNumber = `+${phoneNumber.slice(0, 1)}`;
    
    // +7 (XXX)
    if (phoneNumberLength > 1) {
      formattedNumber += ` (${phoneNumber.slice(1, 4)}`;
    }
    
    // +7 (XXX) XXX
    if (phoneNumberLength >= 5) {
      formattedNumber += `) ${phoneNumber.slice(4, 7)}`;
    }
    
    // +7 (XXX) XXX-XX
    if (phoneNumberLength >= 8) {
      formattedNumber += `-${phoneNumber.slice(7, 9)}`;
    }
    
    // +7 (XXX) XXX-XX-XX
    if (phoneNumberLength >= 10) {
      formattedNumber += `-${phoneNumber.slice(9, 11)}`;
    }
    
    return formattedNumber;
  };

  // Форматирование имени: первая буква каждого слова — заглавная
  const formatName = (value) => {
    // Убираем только цифры и подчёркивания. Буквы любых алфавитов, пробел,
    // дефис и апостроф оставляем: клиенты пишут имена и кириллицей, и
    // латиницей, и на своих языках («Мария-Анна», «O'Brien», «Пётр»).
    // Прошлая версия вырезала «ё» и дефис — «Пётр» превращался в «Птр».
    const cleaned = value.replace(/[\d_]/g, '');

    // Через \b и \w это не работает: в JavaScript они охватывают только ASCII,
    // поэтому в строке из русских букв нет ни одной границы слова и регистр
    // не поднимался вообще — сервер отклонял «иван» как имя с маленькой буквы.
    return cleaned.replace(
      /(^|[\s'-])(\S)/g,
      (match, separator, char) => separator + char.toUpperCase()
    );
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formattedPhone
    }));
    
    // Отслеживаем начало заполнения формы
    if (!formStarted && formattedPhone.length > 3) {
      setFormStarted(true);
      trackGoal(GOALS.FORM_FILL_START);
      trackEvent('Form', 'Start Filling', 'Contact Form');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'phone') {
      handlePhoneChange(e);
    } else if (name === 'name') {
      const formattedName = formatName(value);
      setFormData(prev => ({
        ...prev,
        name: formattedName
      }));
      
      // Отслеживаем начало заполнения формы
      if (!formStarted && formattedName.length > 0) {
        setFormStarted(true);
        trackGoal(GOALS.FORM_FILL_START);
        trackEvent('Form', 'Start Filling', 'Contact Form');
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone) {
      setError('Пожалуйста, заполните все поля');
      trackEvent('Form', 'Validation Error', 'Empty Fields');
      return;
    }

    if (!formData.agreedPdn) {
      setError('Необходимо согласие на обработку персональных данных');
      trackEvent('Form', 'Validation Error', 'No PDN Agreement');
      return;
    }

    if (!formData.agreedPolicy) {
      setError('Необходимо согласие с политикой конфиденциальности');
      trackEvent('Form', 'Validation Error', 'No Policy Agreement');
      return;
    }

    // Проверяем длину номера
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 11) {
      setError('Введите номер телефона полностью — должно быть 11 цифр');
      trackEvent('Form', 'Validation Error', 'Invalid Phone');
      return;
    }

    setStatus('loading');
    setError('');

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/submit-form`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email || '',
        website: formData.website,
        form_started_at: Date.now() - formOpenedAt.current,
      });
      
      if (response.data.success) {
        setStatus('success');
        setFormData({ name: '', phone: '+7', email: '', website: '', agreedPdn: false, agreedPolicy: false });
        setFormStarted(false);
        
        // Отслеживаем успешную отправку
        trackGoal(GOALS.FORM_SUBMIT, {
          form_name: 'contact_form',
          form_location: window.location.pathname
        });
        trackEvent('Form', 'Submit Success', 'Contact Form');
        
        // Также отправляем событие для электронной коммерции (если понадобится в будущем)
        trackEvent('Lead', 'Generated', 'Contact Form', 1);
        
        // Сброс статуса через 3 секунды
        setTimeout(() => {
          setStatus('idle');
        }, 3000);
      } else {
        // Ответ 200 с success: false раньше не обрабатывался, и форма
        // навсегда оставалась в состоянии «Отправляем...»
        setStatus('error');
        setError(response.data.message || 'Произошла ошибка при отправке формы');
        trackEvent('Form', 'Submit Error', 'Success flag false');
      }
    } catch (err) {
      setStatus('error');
      // Сервер присылает конкретную причину отказа («Введите имя буквами»,
      // «Слишком много заявок»). Без неё любая ошибка выглядела одинаково —
      // ни пользователь, ни мы не могли понять, что именно не так.
      setError(
        (err.response && err.response.data && err.response.data.message) ||
        'Произошла ошибка при отправке формы'
      );
      console.error(err);
      
      // Отслеживаем ошибку
      trackEvent('Form', 'Submit Error', err.message || 'Unknown Error');
    }
  };

  return (
    <section id="form" className="contact-form-section">
      <div className="container">
        <div className="form-container">
          <div className="form-header">
            <h2>Остались вопросы? Оставьте заявку!</h2>
            <p>Заполните форму ниже, и наш специалист свяжется с вами в ближайшее рабочее время для бесплатной консультации.</p>
            <LegalNotice variant="consultation" className="form-legal-notice" />
          </div>

          <form onSubmit={handleSubmit} className="contact-form">
            <div className="form-honeypot" aria-hidden="true">
              <label htmlFor="website">Не заполняйте это поле</label>
              <input
                type="text"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex="-1"
              />
            </div>
            <div className="form-group">
              <label htmlFor="name">Ваше имя</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Иван Иванов"
                className="form-input"
                disabled={status === 'loading'}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">Номер телефона</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+7 (999) 999-99-99"
                className="form-input"
                disabled={status === 'loading'}
                maxLength={18}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email <span className="form-optional">(необязательно)</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@mail.ru"
                className="form-input"
                disabled={status === 'loading'}
              />
            </div>

            <div className="form-checkbox">
              <input
                type="checkbox"
                id="agreedPdn"
                name="agreedPdn"
                checked={formData.agreedPdn}
                onChange={handleChange}
                disabled={status === 'loading'}
              />
              <label htmlFor="agreedPdn">
                Я даю{' '}
                <a
                  href="/privacy#consent"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('Link', 'Click', 'PDN Consent')}
                >
                  согласие на обработку персональных данных
                </a>.
              </label>
            </div>

            <div className="form-checkbox">
              <input
                type="checkbox"
                id="agreedPolicy"
                name="agreedPolicy"
                checked={formData.agreedPolicy}
                onChange={handleChange}
                disabled={status === 'loading'}
              />
              <label htmlFor="agreedPolicy">
                Я ознакомлен(-а) и согласен(-на) с{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('Link', 'Click', 'Privacy Policy')}
                >
                  политикой конфиденциальности
                </a>.
              </label>
            </div>

            {error && (
              <div className="form-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              className={`form-submit ${status}`}
              disabled={status === 'loading' || status === 'success'}
            >
              {status === 'idle' && (
                <>
                  Отправить заявку
                  <Send size={20} />
                </>
              )}
              {status === 'loading' && (
                <>
                  Отправляем...
                  <Loader size={20} className="spin" />
                </>
              )}
              {status === 'success' && (
                <>
                  Заявка отправлена!
                  <Check size={20} />
                </>
              )}
            </button>
          </form>

          <div className="form-footer">
            <p>Или позвоните нам напрямую:</p>
            <a 
              href="tel:+79257166607" 
              className="phone-big"
              onClick={() => {
                trackGoal(GOALS.PHONE_CLICK, { phone_location: 'form_footer' });
                trackEvent('Contact', 'Phone Click', 'Form Footer');
              }}
            >
              +7 (925) 716-66-07
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
